import { yupResolver } from "@hookform/resolvers/yup";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Grid,
  InputLabel,
  Paper,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { EditionForm } from "shared/interfaces";
import { newEdition } from "shared/schemas/newEdtion";
import { usePostNewEdicaoMutation } from "shared/features/api/edicao/edicaoSlice";
import { toast } from "react-toastify";

export const AddEditionForm = () => {
  const [gerenciamentoAtivo, setGerenciamentoAtivo] = useState(false);
  const [criarEdicao] = usePostNewEdicaoMutation();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGerenciamentoAtivo(event.target.checked);
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditionForm>({
    resolver: yupResolver(newEdition),
  });

  const onSubmit = async (data: EditionForm) => {
    try {
      await toast.promise(criarEdicao(data).unwrap(), {
        pending: "Criando edição...",
        success: `Edição "${data.nome}" criada com sucesso!`,
        error: {
          render({ data }) {
            const response = data as {
              data?: {
                errors?: string[];
                message?: string;
              };
            };
            const errorMessage =
              response.data?.errors?.[0] ||
              response.data?.message ||
              "Erro ao adicionar edição.";
            return errorMessage;
          },
        },
      });
    } catch (err) {
      console.error("Erro tratado no toast:", err);
    }
  };

  const [dataFimError, setDataFimError] = useState<string | null>(null);
  const [dataInicioError, setDataInicioError] = useState<string | null>(null);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          borderRadius: "5px",
          gap: 2,
          width: {
            md: "100%",
            lg: "50%",
          },
        }}
      >
        <Paper sx={{ padding: "16px", height: "100%" }}>
          <Typography fontSize={20} fontWeight={600} mb={2}>
            Adicionar Edição
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="addEdition-nome">Nome da Edição*</InputLabel>
              <TextField
                fullWidth
                id="addEdition-nome"
                placeholder="Vem Ser 16"
                error={!!errors.nome}
                helperText={errors.nome?.message}
                {...register("nome")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="addEdition-nota">Nota de Corte</InputLabel>
              <TextField
                fullWidth
                id="addEdition-nota"
                placeholder="0.0"
                type="number"
                error={!!errors.notaCorte}
                helperText={errors.notaCorte?.message}
                {...register("notaCorte")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                label="Agendar Inscrições"
                control={
                  <Switch
                    checked={gerenciamentoAtivo}
                    onChange={handleChange}
                  />
                }
              />
            </Grid>

            <Collapse in={gerenciamentoAtivo} style={{ width: "100%" }}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  style={{ paddingTop: "16px", paddingLeft: "16px" }}
                >
                  <InputLabel htmlFor="dataAberturaInscricao">
                    Início das inscrições
                  </InputLabel>
                  <Controller
                    name="dataAberturaInscricao"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(newValue) => {
                          if (newValue && dayjs(newValue).isValid()) {
                            setDataInicioError(null);
                            field.onChange(dayjs(newValue).toISOString());
                          } else {
                            setDataInicioError("Data inválida");
                            field.onChange(null);
                          }
                        }}
                        inputFormat="DD/MM/YYYY HH:mm"
                        renderInput={(params: TextFieldProps) => (
                          <TextField
                            fullWidth
                            {...params}
                            error={!!dataInicioError}
                            helperText={dataInicioError}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  style={{ paddingTop: "16px", paddingLeft: "16px" }}
                >
                  <InputLabel htmlFor="dataFimInscricao">
                    Final das inscrições
                  </InputLabel>
                  <Controller
                    name="dataFimInscricao"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(newValue) => {
                          if (newValue && dayjs(newValue).isValid()) {
                            setDataFimError(null);
                            field.onChange(dayjs(newValue).toISOString());
                          } else {
                            setDataFimError("Data inválida");
                            field.onChange(null);
                          }
                        }}
                        inputFormat="DD/MM/YYYY HH:mm"
                        renderInput={(params: TextFieldProps) => (
                          <TextField
                            fullWidth
                            {...params}
                            error={!!dataFimError}
                            helperText={dataFimError}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Collapse>

            <Grid item xs={12}>
              <Button type="submit" variant="contained" fullWidth>
                Adicionar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};
